import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import EmailIcon from "@mui/icons-material/Email";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";

import api from "../../../../../store/services/api";
import customerService from "../../../../../api/customer";
import {
  validatePhone,
  validateString,
} from "../../../../../common/validators";
import {
  PHONE_HELPER,
  STRING_NOT_NULL_HELPER,
} from "../../../../../common/helpersTxt";
import {
  setCustomer,
  setCartProducts,
} from "../../../../../store/actions/newOrderSlice";
import {
  ButtonDarkGrey,
  ButtonInfoNoShadow,
  ButtonSuccessNoShadow,
} from "../../../../styled/button";

// ─── Shared context ──────────────────────────────────────────────────────────

const CustomerFormContext = createContext(null);

// ─── Animation variants ───────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const resultVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.25, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2, ease: "easeIn" },
  },
};

// ─── Customer result card ─────────────────────────────────────────────────────

const CustomerResultCard = ({ customer, onSelect, theme }) => (
  <motion.div
    variants={itemVariants}
    whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
    whileTap={{ scale: 0.98 }}
  >
    <Box
      onClick={() => onSelect(customer)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: "pointer",
        transition: "border-color 0.2s",
        "&:hover": {
          borderColor: theme.palette.primary.light,
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          flexShrink: 0,
        }}
      >
        <AccountCircleIcon
          sx={{ fontSize: 20, color: theme.palette.primary.main }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
          }}
        >
          {customer.name}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: theme.palette.text.secondary,
            fontFamily: "'SF Mono', 'Cascadia Code', monospace",
            letterSpacing: "0.5px",
          }}
        >
          {customer.phonenmbr}
        </Typography>
      </Box>
      <CheckCircleIcon
        sx={{
          fontSize: 18,
          color: theme.palette.success.main,
          opacity: 0.6,
        }}
      />
    </Box>
  </motion.div>
);

// ─── Past order card ──────────────────────────────────────────────────────────

const ORDER_TYPE_LABELS = {
  DELIVERY: "Delivery",
  TAKEOUT: "Retirada",
  INDOOR: "Mesa",
};

const PastOrderCard = ({ order, onSelect, theme }) => {
  const dateStr = new Date(order.created_at).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  const productsSummary =
    order.products
      .slice(0, 3)
      .map((p) => `${p.quantity > 1 ? `${parseInt(p.quantity)}x ` : ""}${p.title}`)
      .join(", ") + (order.products.length > 3 ? ` +${order.products.length - 3}` : "");

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
      whileTap={{ scale: 0.98 }}
    >
      <Box
        onClick={() => onSelect(order)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          p: 1.5,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          cursor: "pointer",
          transition: "border-color 0.2s",
          "&:hover": {
            borderColor: theme.palette.primary.light,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              flexShrink: 0,
            }}
          >
            <ReceiptLongIcon
              sx={{ fontSize: 18, color: theme.palette.warning.dark }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: 1.3,
                }}
              >
                #{order.order_index}
              </Typography>
              <Chip
                label={ORDER_TYPE_LABELS[order.order_type] || order.order_type}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.dark,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: theme.palette.text.secondary,
                fontFamily: "'SF Mono', 'Cascadia Code', monospace",
                letterSpacing: "0.3px",
              }}
            >
              {dateStr}
            </Typography>
          </Box>
        </Box>

        <Typography
          sx={{
            fontSize: "0.75rem",
            color: theme.palette.text.secondary,
            lineHeight: 1.4,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            pl: 0.5,
          }}
        >
          {productsSummary}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pl: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 700,
              color: theme.palette.success.dark,
            }}
          >
            R$ {order.total.toFixed(2)}
          </Typography>
          <Chip
            icon={<ReplayIcon sx={{ fontSize: "12px !important" }} />}
            label="Repetir"
            size="small"
            sx={{
              height: 22,
              fontSize: "0.68rem",
              fontWeight: 600,
              cursor: "pointer",
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.dark,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
              },
            }}
          />
        </Box>
      </Box>
    </motion.div>
  );
};

// ─── Form field with icon adornment ───────────────────────────────────────────

const FormField = ({
  icon: Icon,
  label,
  value,
  onChange,
  error,
  helperText,
  disabled,
  required,
  maxLength,
  autoFocus,
}) => (
  <motion.div variants={itemVariants}>
    <TextField
      size="small"
      variant="outlined"
      label={label}
      fullWidth
      required={required}
      error={error}
      value={value || ""}
      disabled={disabled}
      helperText={helperText}
      onChange={onChange}
      autoFocus={autoFocus}
      inputProps={{ maxLength }}
      InputProps={{
        startAdornment: Icon ? (
          <InputAdornment position="start">
            <Icon sx={{ fontSize: 18, opacity: 0.5 }} />
          </InputAdornment>
        ) : undefined,
      }}
    />
  </motion.div>
);

// ─── Provider (wraps modal so body + footer share state) ──────────────────────

export function CustomerFormProvider({ children }) {
  const dispatch = useDispatch();

  const customer = useSelector((state) => state.newOrder.customer);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);

  const [ignoreAddCustomer, setIgnoreAddCustomer] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [foundCustomer, setFoundCustomer] = useState(false);
  const [showFullForm, setShowFullForm] = useState(false);

  const [pastOrders, setPastOrders] = useState([]);
  const [loadingPastOrders, setLoadingPastOrders] = useState(false);

  const [customerId, setCustomerId] = useState(null);
  const [channelCustomerId, setChannelCustomerId] = useState("");
  const [name, setName] = useState("");
  const [isNameValid, setIsNameValid] = useState(false);

  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");

  const [phoneNumber, setPhoneNumber] = useState(null);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  useEffect(() => {
    if (customer) {
      setCustomerId(customer.customer_id);
      setChannelCustomerId(customer.channel_customer_id);
      setName(customer.name);
      setEmail(customer.email);
      setPhoneNumber(customer.phonenmbr);
      setCpf(customer.cpf);
      setIgnoreAddCustomer(customer.ignoreAddCustomer);
      setShowFullForm(!customer.ignoreAddCustomer);
      setHasSearched(true);
      setFoundCustomer(true);
    }
  }, []);

  useEffect(() => {
    if (phoneNumber) setIsPhoneValid(validatePhone(phoneNumber));
    if (name) setIsNameValid(validateString(name));
    if ((phoneNumber?.length > 0 || name?.length > 0) && !ignoreAddCustomer) {
      setHasTyped(true);
    }
  }, [phoneNumber, name, ignoreAddCustomer]);

  const handlePhoneChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  const validateCustomerData = useCallback(
    ({ validatePhone: doPhone = true, validateName: doName = true }) => {
      if (ignoreAddCustomer) return true;

      setHasTyped(true);

      if (doPhone) {
        if (!phoneNumber) return false;
        const valid = validatePhone(phoneNumber);
        setIsPhoneValid(valid);
        if (!valid) return false;
      }

      if (doName) {
        if (!name) return false;
        const valid = validateString(name);
        setIsNameValid(valid);
        if (!valid) return false;
      }

      return true;
    },
    [ignoreAddCustomer, phoneNumber, name]
  );

  const searchForConsumerByPhone = useCallback(async () => {
    const isValid = validateCustomerData({
      validatePhone: true,
      validateName: false,
    });
    if (!isValid) return;

    try {
      setLoading(true);
      const res = await api.get("/customers", {
        params: { phone: phoneNumber },
      });

      if (res.data.length === 0) {
        setFoundCustomer(false);
        setHasSearched(true);
        setLoading(false);
        return;
      }

      setCustomers(res.data);
      setFoundCustomer(true);
      setHasSearched(true);
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  }, [validateCustomerData, phoneNumber]);

  const selectCustomer = (selectedCustomer) => {
    setCustomerId(selectedCustomer.customer_id);
    setChannelCustomerId(selectedCustomer.channel_customer_id);
    setName(selectedCustomer.name);
    setCpf(selectedCustomer.cpf);
    setEmail(selectedCustomer.email);
    setPhoneNumber(selectedCustomer.phonenmbr);
    setShowFullForm(true);

    if (selectedCustomer.customer_id) {
      setLoadingPastOrders(true);
      customerService
        .getCustomerPastOrders(selectedCustomer.customer_id)
        .then((res) => {
          setPastOrders(res.data || []);
        })
        .catch(() => {
          setPastOrders([]);
        })
        .finally(() => {
          setLoadingPastOrders(false);
        });
    }
  };

  const handleSelectPastOrder = useCallback(
    (order) => {
      const cartItems = order.products.map((p) => ({
        product_identifier: p.product_identifier,
        product_id: p.product_id,
        title: p.title,
        price: p.price,
        un_price: p.un_price,
        quantity: p.quantity,
        total_price: p.total_price,
        unit: p.unit,
        observations: p.observations,
        complementsPrice: p.complementsPrice,
        complements: (p.complements || []).map((c) => ({
          id: c.id,
          complement_id: c.complement_id,
          title: c.title,
          price: c.price,
          quantity: c.quantity,
        })),
      }));

      dispatch(setCartProducts(cartItems));

      const customer_obj = {
        customer_id: customerId,
        cpf,
        channel_customer_id: channelCustomerId,
        email,
        name,
        phonenmbr: phoneNumber,
        ignoreAddCustomer,
        shouldCreate: false,
      };
      dispatch(setCustomer(customer_obj));
    },
    [
      dispatch,
      customerId,
      cpf,
      channelCustomerId,
      email,
      name,
      phoneNumber,
      ignoreAddCustomer,
    ]
  );

  const showFullFormManually = () => {
    setShowFullForm(true);
    setHasSearched(true);
  };

  const hideFullForm = () => {
    setShowFullForm(false);
    setHasSearched(false);
  };

  const handleDontAddCustomer = (event) => {
    const value = event.target.checked;
    if (value) {
      setFoundCustomer(true);
      setHasSearched(true);
    }
    setIgnoreAddCustomer(value);
  };

  const handleSubmit = useCallback(() => {
    const isValid = validateCustomerData({});
    if (!isValid) return;

    const customer_obj = {
      customer_id: customerId,
      cpf,
      channel_customer_id: channelCustomerId,
      email,
      name,
      phonenmbr: phoneNumber,
      ignoreAddCustomer,
    };
    const shouldCreate = !foundCustomer;

    dispatch(setCustomer({ ...customer_obj, shouldCreate }));
  }, [
    validateCustomerData,
    foundCustomer,
    dispatch,
    customerId,
    cpf,
    channelCustomerId,
    email,
    name,
    phoneNumber,
    ignoreAddCustomer,
  ]);

  useEffect(() => {
    const handleKeyPress = async (event) => {
      if (event.key === "Enter") {
        if (!customerId) {
          await searchForConsumerByPhone();
        } else {
          handleSubmit();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [customerId, searchForConsumerByPhone, handleSubmit]);

  const showSearchResults = hasSearched && !showFullForm && !ignoreAddCustomer;
  const showDetailFields = showFullForm || ignoreAddCustomer;
  const phoneDisabled = showFullForm && hasSearched && foundCustomer;

  const ctxValue = useMemo(
    () => ({
      loading,
      hasSearched,
      hasTyped,
      ignoreAddCustomer,
      customers,
      foundCustomer,
      showFullForm,
      customerId,
      name,
      isNameValid,
      email,
      cpf,
      phoneNumber,
      isPhoneValid,
      showSearchResults,
      showDetailFields,
      phoneDisabled,
      pastOrders,
      loadingPastOrders,
      handlePhoneChange,
      searchForConsumerByPhone,
      selectCustomer,
      showFullFormManually,
      hideFullForm,
      handleDontAddCustomer,
      handleSubmit,
      handleSelectPastOrder,
      setName,
      setEmail,
      setCpf,
    }),
    [
      loading,
      hasSearched,
      hasTyped,
      ignoreAddCustomer,
      customers,
      foundCustomer,
      showFullForm,
      customerId,
      name,
      isNameValid,
      email,
      cpf,
      phoneNumber,
      isPhoneValid,
      showSearchResults,
      showDetailFields,
      phoneDisabled,
      pastOrders,
      loadingPastOrders,
      searchForConsumerByPhone,
      handleSubmit,
      handleSelectPastOrder,
    ]
  );

  return (
    <CustomerFormContext.Provider value={ctxValue}>
      {children}
    </CustomerFormContext.Provider>
  );
}

// ─── Footer (pinned at bottom of modal, outside ModalContent) ─────────────────

export function CustomerFormFooter() {
  const ctx = useContext(CustomerFormContext);
  const theme = useTheme();

  if (!ctx) return null;

  const {
    loading,
    showFullForm,
    showDetailFields,
    ignoreAddCustomer,
    handleSubmit,
    searchForConsumerByPhone,
    hideFullForm,
    handleDontAddCustomer,
  } = ctx;

  return (
    <Box
      sx={{
        p: 2.5,
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        background: "#f9fafb",
      }}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Box sx={{ display: "flex", gap: 1 }}>
              {showFullForm && (
                <ButtonDarkGrey
                  startIcon={
                    <ArrowBackIcon sx={{ fontSize: "16px !important" }} />
                  }
                  onClick={hideFullForm}
                  sx={{ flex: "0 0 auto", minWidth: 100 }}
                >
                  Voltar
                </ButtonDarkGrey>
              )}

              {showDetailFields ? (
                <ButtonSuccessNoShadow
                  fullWidth
                  startIcon={<NavigateNextIcon />}
                  onClick={handleSubmit}
                >
                  Selecionar Cliente
                </ButtonSuccessNoShadow>
              ) : (
                <ButtonInfoNoShadow
                  fullWidth
                  startIcon={<SearchIcon />}
                  onClick={searchForConsumerByPhone}
                >
                  Pesquisar Cliente
                </ButtonInfoNoShadow>
              )}
            </Box>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 0.5,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ignoreAddCustomer}
                    onChange={handleDontAddCustomer}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Não Cadastrar Cliente
                  </Typography>
                }
              />
            </Box>
          </motion.div>
        </motion.div>
      )}
    </Box>
  );
}

// ─── Form body (scrollable inside ModalContent) ───────────────────────────────

export default function FormCustomer() {
  const ctx = useContext(CustomerFormContext);
  const theme = useTheme();

  const {
    ignoreAddCustomer,
    isPhoneValid,
    hasTyped,
    phoneNumber,
    phoneDisabled,
    loading,
    showFullForm,
    showSearchResults,
    showDetailFields,
    foundCustomer,
    customers,
    name,
    isNameValid,
    cpf,
    email,
    pastOrders,
    loadingPastOrders,
    handlePhoneChange,
    searchForConsumerByPhone,
    selectCustomer,
    showFullFormManually,
    handleSelectPastOrder,
    setName,
    setCpf,
    setEmail,
  } = ctx;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* ── Search section ─────────────────────────────────────────────────── */}
      <Box sx={{ p: 2.5, pb: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <TextField
              size="small"
              variant="outlined"
              label="Telefone"
              fullWidth
              required={!ignoreAddCustomer}
              error={!isPhoneValid && hasTyped}
              value={phoneNumber || ""}
              disabled={phoneDisabled}
              helperText={!isPhoneValid && hasTyped ? PHONE_HELPER : ""}
              onChange={handlePhoneChange}
              autoFocus
              inputProps={{ maxLength: 11 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                  </InputAdornment>
                ),
                endAdornment: !showFullForm ? (
                  <InputAdornment position="end">
                    {loading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Chip
                        icon={
                          <SearchIcon sx={{ fontSize: "14px !important" }} />
                        }
                        label="Buscar"
                        size="small"
                        onClick={searchForConsumerByPhone}
                        sx={{
                          height: 26,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.08
                          ),
                          color: theme.palette.primary.dark,
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.15
                            ),
                          },
                        }}
                      />
                    )}
                  </InputAdornment>
                ) : null,
              }}
            />
          </motion.div>
        </motion.div>
      </Box>

      {/* ── Search results ─────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {showSearchResults && (
          <motion.div
            key="search-results"
            variants={resultVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box sx={{ px: 2.5, py: 2.5 }}>
              {foundCustomer ? (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.div variants={itemVariants}>
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        color: theme.palette.text.secondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        mb: 2,
                      }}
                    >
                      Clientes encontrados ({customers.length})
                    </Typography>
                  </motion.div>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      maxHeight: 220,
                      overflowY: "auto",
                      pt: 0.5,
                    }}
                  >
                    {customers.map((c) => (
                      <CustomerResultCard
                        key={c.customer_id || c.phonenmbr}
                        customer={c}
                        onSelect={selectCustomer}
                        theme={theme}
                      />
                    ))}
                  </Box>
                </motion.div>
              ) : (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.div variants={itemVariants}>
                    <Alert
                      severity="warning"
                      sx={{ borderRadius: 1.5, mb: 1.5 }}
                    >
                      Nenhum cliente encontrado
                    </Alert>
                  </motion.div>

                  <motion.div
                    variants={itemVariants}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Box
                      onClick={showFullFormManually}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px dashed ${theme.palette.primary.light}`,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.02
                        ),
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05
                          ),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                        }}
                      >
                        <PersonAddIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.84rem",
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }}
                      >
                        Cadastrar Novo Cliente
                      </Typography>
                    </Box>
                  </motion.div>
                </motion.div>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Detail fields ──────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {showDetailFields && (
          <motion.div
            key="detail-fields"
            variants={resultVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box
              sx={{
                px: 2.5,
                py: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                style={{ display: "flex", flexDirection: "column", gap: "14px" }}
              >
                {showFullForm && (
                  <motion.div variants={itemVariants}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          color: theme.palette.text.secondary,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {foundCustomer ? "Dados do cliente" : "Novo cliente"}
                      </Typography>
                    </Box>
                  </motion.div>
                )}

                <FormField
                  icon={PersonIcon}
                  label="Nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!isNameValid && hasTyped}
                  helperText={
                    !isNameValid && hasTyped ? STRING_NOT_NULL_HELPER : ""
                  }
                  required={!ignoreAddCustomer}
                />

                {showFullForm && (
                  <>
                    <FormField
                      icon={BadgeIcon}
                      label="CPF"
                      value={cpf}
                      onChange={(e) => setCpf(e.target.value)}
                    />
                    <FormField
                      icon={EmailIcon}
                      label="E-mail"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </>
                )}
              </motion.div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Past orders ────────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {showFullForm && foundCustomer && (
          <motion.div
            key="past-orders"
            variants={resultVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 2.5,
                  pb: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <ReceiptLongIcon
                  sx={{
                    fontSize: 14,
                    color: theme.palette.text.secondary,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: theme.palette.text.secondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Últimos pedidos
                </Typography>
              </Box>

              {loadingPastOrders ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 2,
                  }}
                >
                  <CircularProgress size={22} />
                </Box>
              ) : pastOrders.length > 0 ? (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      maxHeight: 240,
                      overflowY: "auto",
                    }}
                  >
                    {pastOrders.map((order) => (
                      <PastOrderCard
                        key={order.order_id}
                        order={order}
                        onSelect={handleSelectPastOrder}
                        theme={theme}
                      />
                    ))}
                  </Box>
                </motion.div>
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: theme.palette.text.disabled,
                    textAlign: "center",
                    py: 1.5,
                  }}
                >
                  Nenhum pedido anterior
                </Typography>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
